"use client";

import { useWebMcpTool } from "@/lib/webmcp/react";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import {
  SEASONAL_SCENARIO_LABEL_FALLBACKS as SCENARIO_FALLBACKS,
  SEASONAL_SCENARIO_LABEL_KEYS as SCENARIO_LABEL_KEYS,
} from "@/lib/funnel-planner/explanation-copy";
import { funnelText } from "@/lib/funnel-planner/i18n";
import type { ManualSeasonalScenarioResult } from "@/lib/funnel-planner/seasonal-scenarios";
import {
  SEASONAL_PLANNING_SCENARIOS,
  type SeasonalDemand,
  type SeasonalPlanningScenario,
} from "@/lib/funnel-planner/schema";
import { z } from "zod";

const seasonalScenarioInput = z.object({
  enabled: z.boolean(),
  lowerPercent: z.number().min(-90).max(300).nullable(),
  expectedPercent: z.number().min(-90).max(300).nullable(),
  upperPercent: z.number().min(-90).max(300).nullable(),
}).strict();

const PERCENT_FIELD: Readonly<Record<SeasonalPlanningScenario, keyof SeasonalDemand>> = {
  lower: "lowerPercent",
  expected: "expectedPercent",
  upper: "upperPercent",
};

/**
 * NL-010 manual seasonal scenario surface.
 *
 * Every number on this panel comes from a percentage the visitor typed. The copy
 * says so explicitly, and the source-backed profile note states that market data
 * remains switched off pending approval, so nothing here implies SEOH is
 * supplying seasonality evidence.
 */
export function SeasonalScenarioPanel({
  content,
  onChange,
  result,
  seasonalDemand,
  readOnly = false,
}: {
  content: FunnelPlannerContent;
  onChange: (next: SeasonalDemand) => void;
  result: ManualSeasonalScenarioResult;
  seasonalDemand: SeasonalDemand;
  /** When viewing another visitor's shared plan, the assumptions are not editable. */
  readOnly?: boolean;
}) {
  useWebMcpTool({
    action: "apply_advisory",
    instance: "seasonal_scenarios",
    schema: seasonalScenarioInput,
    enabled: !readOnly,
    execute: (input) => {
      if (readOnly) return { status: "unavailable" as const };

      onChange({
        ...seasonalDemand,
        enabled: input.enabled,
        assumptionSource: input.enabled ? "manual" : "none",
        scenario: input.enabled ? seasonalDemand.scenario ?? "expected" : null,
        multiplier: input.enabled ? seasonalDemand.multiplier : "1.00",
        lowerPercent: input.lowerPercent,
        expectedPercent: input.expectedPercent,
        upperPercent: input.upperPercent,
      });

      return {
        status: "success" as const,
        uiState: { enabled: input.enabled, readiness: result.readiness },
      };
    },
  });

  function setPercent(scenario: SeasonalPlanningScenario, raw: string) {
    if (readOnly) return;
    const parsed = raw.trim() === "" ? null : Number(raw);

    onChange({
      ...seasonalDemand,
      [PERCENT_FIELD[scenario]]: parsed === null || Number.isNaN(parsed) ? null : parsed,
    });
  }

  function toggleEnabled(enabled: boolean) {
    if (readOnly) return;
    onChange({
      ...seasonalDemand,
      enabled,
      assumptionSource: enabled ? "manual" : "none",
      scenario: enabled ? seasonalDemand.scenario ?? "expected" : null,
      multiplier: enabled ? seasonalDemand.multiplier : "1.00",
    });
  }

  const blocked = result.readiness === "blocked";
  const blockersDescriptionId = "seasonal-scenario-blockers";

  return (
    <section
      aria-label={funnelText(content, "seasonalScenariosSectionAria", "Seasonal planning scenarios")}
      className="rounded-xl border bg-muted/30 p-4"
      data-funnel-planner-seasonal-scenarios="true"
      data-seasonal-enabled={seasonalDemand.enabled ? "true" : "false"}
      data-seasonal-readiness={result.readiness}
      data-seasonal-revenue-multiplier-applied={String(result.revenueMultiplierApplied)}
    >
      <h3 className="text-base font-bold">
        {funnelText(content, "seasonalScenariosTitle", "Seasonal planning scenarios")}
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {funnelText(
          content,
          "seasonalScenariosIntro",
          "Plan for a slow month, a normal month, and a strong month by saying how much your visitor volume moves.",
        )}
      </p>

      <label className="mt-3 flex items-center gap-2 text-sm">
        <input
          checked={seasonalDemand.enabled}
          disabled={readOnly}
          onChange={(event) => toggleEnabled(event.target.checked)}
          type="checkbox"
        />
        <span>
          {funnelText(content, "seasonalScenariosEnable", "Add a seasonal planning assumption")}
        </span>
      </label>

      {seasonalDemand.enabled ? (
        <>
          <p className="mt-2 rounded-lg border bg-muted/40 p-2 text-xs text-muted-foreground">
            {funnelText(
              content,
              "seasonalScenariosOwnedNote",
              "These percentages are your own planning assumptions. We are not supplying market or seasonality data here.",
            )}
          </p>

          <div className="mt-3 grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
            {SEASONAL_PLANNING_SCENARIOS.map((scenario) => {
              const output = result.scenarios.find((entry) => entry.scenario === scenario);
              const value = seasonalDemand[PERCENT_FIELD[scenario]];
              const scenarioLabel = funnelText(
                content,
                SCENARIO_LABEL_KEYS[scenario],
                SCENARIO_FALLBACKS[scenario],
              );

              return (
                <div
                  className="rounded-lg border bg-muted/20 p-3"
                  data-seasonal-scenario={scenario}
                  key={scenario}
                >
                  <p className="text-xs font-semibold text-muted-foreground">{scenarioLabel}</p>
                  <label className="mt-1 block text-xs text-muted-foreground">
                    {/*
                      Scenario-qualified so the three inputs do not share one
                      accessible name; a screen reader announces which month the
                      percentage belongs to.
                    */}
                    <span className="sr-only">
                      {`${scenarioLabel}: ${funnelText(content, "seasonalScenarioPercentLabel", "Change in monthly visitors (%)")}`}
                    </span>
                    <input
                      aria-describedby={blocked ? blockersDescriptionId : undefined}
                      aria-invalid={blocked || undefined}
                      className="mt-1 w-full rounded-md border bg-background px-2 py-1 text-sm"
                      disabled={readOnly}
                      inputMode="decimal"
                      max={300}
                      min={-90}
                      onChange={(event) => setPercent(scenario, event.target.value)}
                      step={1}
                      type="number"
                      value={typeof value === "number" ? value : ""}
                    />
                  </label>
                  {result.readiness === "ready" && output ? (
                    <p
                      aria-live="polite"
                      className="mt-2 text-xs text-muted-foreground"
                      data-seasonal-scenario-visitors={output.monthlyVisitors}
                      role="status"
                    >
                      <span className="text-muted-foreground">
                        {funnelText(content, "seasonalScenarioVisitorsLabel", "Planned monthly visitors")}:{" "}
                      </span>
                      <span className="font-bold text-foreground">{output.monthlyVisitors.toLocaleString()}</span>
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>

          {blocked ? (
            <div
              className="mt-3 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive"
              id={blockersDescriptionId}
              role="alert"
            >
              <p className="font-semibold">
                {funnelText(content, "seasonalScenariosBlockedTitle", "Check these assumptions")}
              </p>
              <ul className="mt-1 list-disc" style={{ paddingInlineStart: 18 }}>
                {result.blockers.map((blocker) => (
                  <li key={blocker}>
                    {funnelText(
                      content,
                      `explanationBlocker.${blocker}`,
                      funnelText(
                        content,
                        "calculationExplanationUnavailableDetail",
                        "Additional detail is unavailable.",
                      ),
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <p className="mt-2 text-xs text-muted-foreground">
            {funnelText(
              content,
              "seasonalScenariosDemandOnlyNote",
              "Applied to monthly visitors only. Revenue moves only because visits move, never on its own.",
            )}
          </p>
        </>
      ) : null}

      <p className="mt-2 text-xs text-muted-foreground">
        {funnelText(
          content,
          "seasonalScenariosSourceDisabledNote",
          "Market-sourced seasonal profiles stay switched off until a reviewed data source is approved.",
        )}
      </p>
    </section>
  );
}
